// src/hooks/useEndConversation.js
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import toast from 'react-simple-toasts';
import { useSharedState } from './SharedStateContext';
import { trackConversationEnded } from '../utilities/tracking';
import { _config } from '../Constants';
import { updateConversationInFirestore, getFirestoreDocId, getCurrentUserId, getCurrentUserEmail, auth, resetFirestoreDocId, updateCachedUserLevel, updateCachedPlacementTest } from '../utilities/firebase'; // Import Firestore update function
import { clearActivityCompletionRecord, endActivityAttempt, isActivityCompletionRecorded } from '../utilities/activityTracking';
import { notifyActivityProgressUpdate } from '../utilities/activitySelection';
import { useTimer } from './TimerContext';
import { normalizeLevel } from '../utilities/levelManager';

const getLocalDateKey = (date = new Date()) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0'),
].join('-');

/**
 * True when an upload failure is the expected consequence of the page going
 * away, not an application bug. The browser aborts in-flight XHR during unload,
 * surfacing as a response-less network error (status 0). These are recovered by
 * the daily emailSweep — the conversation is already persisted to Firestore — so
 * they must not be reported to Sentry as errors (Sentry FRANCOFLEX-BY).
 *
 * @param {Error} error - The caught axios error.
 * @param {boolean} viaUnload - Whether the call originated from a beforeunload handler.
 * @returns {boolean}
 */
const isExpectedUnloadUploadError = (error, viaUnload) => {
  // A network-class failure has no HTTP response — the request never completed.
  const isNetworkError = !error?.response && (
    error?.code === 'ERR_NETWORK' ||
    error?.code === 'ECONNABORTED' ||
    /network error/i.test(error?.message || '')
  );
  if (!isNetworkError) return false;

  const pageHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  return Boolean(viaUnload || pageHidden);
};

/**
 * Conversation documents whose /end has been claimed by a caller.
 *
 * /end is not idempotent: it dispatches the analysis Cloud Function, which writes the
 * analysis, may set the learner's level and sends the results email. Running it twice
 * for one conversation means two analyses and two emails.
 *
 * The guard lives at module scope because Header and ConversationWT each hold their own
 * instance of this hook, and `uploadInProgressRef` is per-instance — it cannot see an
 * upload started by the other component. That matters now that a completed activity ends
 * itself: the learner can still hit the header's "Activities" button, or close the tab,
 * while that first /end is in flight.
 */
const claimedConversationIds = new Set();

/**
 * Claim the single /end allowed for a conversation.
 * @param {string|null} conversationId - Firestore conversation id, null before one exists.
 * @returns {boolean} true when the caller may proceed; false when it is a duplicate.
 */
export const claimConversationEnd = (conversationId) => {
  // No document id yet (a conversation that never got one): nothing to dedupe against,
  // and the per-instance in-progress ref still guards the common double-click case.
  if (!conversationId) return true;
  if (claimedConversationIds.has(conversationId)) return false;
  claimedConversationIds.add(conversationId);
  return true;
};

/**
 * Release a claim after an upload failed, so a later attempt can retry.
 * @param {string|null} conversationId
 */
export const releaseConversationEnd = (conversationId) => {
  if (conversationId) claimedConversationIds.delete(conversationId);
};

/** Test seam: forget every claim. */
export const resetConversationEndClaims = () => claimedConversationIds.clear();

const useEndConversation = () => {
  const { sharedState, setSharedState } = useSharedState();
  const { stopActivityTimer } = useTimer();
  const { t } = useTranslation();
  const URL = _config.url;
  const HTTP_end = `${URL}end`;

  // Guard against duplicate endConversation calls
  const uploadInProgressRef = useRef(false);

  const uploadConversation = useCallback(async (
    conversation,
    elevenLabsConversationId,
    feedbackData,
    conversationContext = {}
  ) => {
    const userId = getCurrentUserId();
    const userEmail = getCurrentUserEmail() || localStorage.getItem('capturedEmail');

    if (!userId) {
      console.error('No user ID found, can\'t upload conversation for txt/analysis.');
      return;
    }

    // Email is optional - analysis/memory will still be saved, just no email report sent
    if (!userEmail) {
      console.warn('No user email found, analysis will be saved but no email report will be sent.');
    }

    const conversationId = getFirestoreDocId();

    // Short sessions skip /end analysis but still close the attempt.
    if (conversation.length <= 2) {
      const selectedActivity = localStorage.getItem('selectedActivity')
        ?? conversationContext.selectedActivity
        ?? null;
      await endActivityAttempt(selectedActivity);

      console.log('Conversation has less than or equal to 2 entries, skipping upload.');
      return;
    }

    try {
      const data = {
        userId: userId, // Firebase Id
        conversationId,
        conversation: JSON.stringify(conversation),
        activityId: localStorage.getItem('selectedActivity') ?? conversationContext.selectedActivity ?? null,
        userLanguage: localStorage.getItem('userLanguage') || conversationContext.userLanguage || 'en', // User's native language
        targetLanguage: conversationContext.targetLanguage || 'French', // Language being learned
        activityCompleted: isActivityCompletionRecorded(conversationId),
        // Keep the learner's local calendar date as the dictionary key in the
        // conversation document. The backend validates it before writing.
        learningProgressDate: getLocalDateKey(),
        conversationProgress: conversationContext.conversationProgress || null,
      };

      // Add email if available (optional - used for sending analysis report)
      if (userEmail) {
        data.email = userEmail;
      }

      if (elevenLabsConversationId) {
        data.elevenConversationId = elevenLabsConversationId; // Add elevenLabsConversationId if it has a value
      }

      // Add feedback if provided
      if (feedbackData) {
        data.feedback = feedbackData;
      }

      // Prepare headers with authentication if user is signed in
      const headers = {};
      const user = auth.currentUser;
      if (user) {
        try {
          const idToken = await user.getIdToken();
          headers.Authorization = `Bearer ${idToken}`;
        } catch (tokenError) {
          console.warn('Failed to get ID token, proceeding without authentication:', tokenError);
        }
      }

      const response = await axios.post(HTTP_end, data, { headers });
      return response.data; // Return full response to access operations.analysis.estimated_level
    } catch (error) {
      console.error('Error uploading conversation:', error);
      error.message = `Failed to upload conversation: ${error.message}`;
      throw error;
    }
  }, [HTTP_end]);

  /**
   * Updates SharedState with new level from placement test results
   * Backend has already saved level to Firestore - just update caches and UI
   */
  const updateLevelFromPlacementTest = useCallback((estimatedLevel) => {
    if (!estimatedLevel || !estimatedLevel.score_average) {
      console.warn('No valid placement test level in response');
      return;
    }

    // Extract the level (score_average is already in 1-12 Quebec scale from AI prompt)
    const score = estimatedLevel.score_average;
    const newLevel = Math.min(12, Math.max(1, Math.round(score)));

    // Update firebase.js cache to prevent stale value from overwriting new level
    updateCachedUserLevel(newLevel);

    // Update placement test cache with new result
    updateCachedPlacementTest({
      date: new Date(),
      estimated_level: estimatedLevel,
      score: score
    });

    // Update SharedState with new level (backend already saved to Firestore).
    // plannerReplanRequestedAt signals PlannerContext to regenerate the learning plan at the
    // corrected level (same objective) so its activities are no longer above/below the user.
    setSharedState(prevState => ({
      ...prevState,
      userLevel: normalizeLevel(newLevel),
      plannerReplanRequestedAt: Date.now()
    }));

    console.log('User level updated in UI after placement test (backend saved to Firestore):', newLevel);

    // Show toast notification about email results
    toast('Shortly after we end the test you will receive a detailed summary and estimated level');
  }, [setSharedState]);

  /**
   * Clear the finished conversation so the next activity starts from a clean slate:
   * a fresh Firestore document, no carried-over transcript, no selected activity.
   */
  const resetConversationState = useCallback(() => {
    // Reset firestoreDocId to ensure new conversation document for next activity
    resetFirestoreDocId();

    setSharedState(prevState => ({
      ...prevState,
      loading: false,
      recording: false,
      talking: false,
      lastRecordedBlob: null,
      firstUserInput: false,
      showActivities: true,
      selectedActivity: null,
      chatHistory: [], // Set chatHistory to an empty array
      conversationProgress: null,
      timerId: null, // Timer for activities.
      remainingTime: 0, // Add remainingTime to sharedState
    }));
  }, [setSharedState]);

  const endConversation = useCallback(async ({
    resetState = true,
    conversation,
    elevenLabsConversationId,
    feedbackData,
    viaUnload = false
  } = {}) => {
    // Guard against duplicate calls - if upload is already in progress, skip
    if (uploadInProgressRef.current) {
      console.log('⚠️ endConversation already in progress, skipping duplicate call');
      return;
    }

    // Guard against a second /end for the same conversation from another component
    // (completion modal, header button, tab close) — see claimConversationEnd.
    const claimedConversationId = getFirestoreDocId();
    if (!claimConversationEnd(claimedConversationId)) {
      console.log('⚠️ Conversation already ended, skipping duplicate /end:', claimedConversationId);
      // The upload must not run twice, but the caller still expects the teardown it
      // asked for — without it the next activity would inherit this transcript.
      if (resetState) resetConversationState();
      return;
    }

    // const isConfirmed = window.confirm(t('end_conversation'));
    // if (isConfirmed) {
    let closeToast = null;
    let response = null;
    try {
      // Set upload in progress flag
      uploadInProgressRef.current = true;

      console.log('🚨 endConversation CALLED - Stack trace:', new Error().stack);
      // Use sharedState.chatHistory as a fallback if conversation is undefined or empty
      const chatHistory = Array.isArray(conversation) && conversation.length > 0
        ? conversation
        : sharedState.chatHistory;

      // console.log(chatHistory);

      if (!Array.isArray(chatHistory) || chatHistory.length === 0) {
        console.log('No valid conversation data available, skipping upload.');
        uploadInProgressRef.current = false; // Reset flag
        return;
      }

      console.log("endConversation with elevenLabsId " + elevenLabsConversationId);
      if (feedbackData) {
        console.log("User feedback:", feedbackData);
      }

      // Show toast while sending conversation for analysis (only for conversations that will be uploaded)
      if (chatHistory.length > 2) {
        closeToast = toast(t('sending_for_analysis'), { duration: Infinity });
      }

      response = await uploadConversation(chatHistory, elevenLabsConversationId, feedbackData, {
        selectedActivity: sharedState.selectedActivity,
        userLanguage: sharedState.userLanguage,
        targetLanguage: sharedState.targetLanguage,
        conversationProgress: sharedState.conversationProgress,
      });
      console.log('Conversation uploaded. Response:', response);

      const selectedActivity = localStorage.getItem('selectedActivity') ?? sharedState.selectedActivity ?? null;
      const activityProgress = response?.operations?.activityProgress;
      const activityMatch = typeof selectedActivity === 'string'
        ? selectedActivity.match(/^learning-path-(\d+)$/)
        : null;
      if (activityMatch && activityProgress?.success && activityProgress.progressIndex !== null) {
        notifyActivityProgressUpdate({
          userId: getCurrentUserId(),
          activityId: activityMatch[1],
          progress: {
            status: activityProgress.status || 'in_progress',
            // Cards and the plan bar show the best attempt
            progressIndex: activityProgress.bestProgressIndex ?? activityProgress.progressIndex,
            lastActivityAt: Date.now(),
          },
        });
      }

      trackConversationEnded(response?.operations?.transcript?.url, sharedState.selectedActivity, feedbackData);
      const updateData = { chatHistory }; // Prepare data for Firestore update
      if (elevenLabsConversationId) {
        updateData.elevenConversationId = elevenLabsConversationId; // Add elevenLabsConversationId if it has a value
      }
      // Add feedback to Firestore update if provided
      if (feedbackData) {
        updateData.feedback = feedbackData;
      }
      await updateConversationInFirestore(chatHistory, feedbackData); // Update conversation in Firestore
      clearActivityCompletionRecord(getFirestoreDocId());

      // Handle placement test completion
      if (sharedState.selectedActivity === 'placement-test') {
        // Clear session flag - test completed, allow re-prompting in future sessions if needed
        sessionStorage.removeItem('placementTestStartedThisSession');
        console.log('Placement test completed - cleared session flag');

        // Update UI with new level from placement test (if available immediately)
        if (response?.operations?.analysis?.estimated_level) {
          updateLevelFromPlacementTest(response.operations.analysis.estimated_level);
        }

        // Show modal to inform user results will be sent via email
        // Skip in activity page mode - ActivityApp handles results display inline
        if (!sharedState.activityPageMode) {
          setSharedState(prevState => ({
            ...prevState,
            showTestResultsModal: true
          }));
        }
      }

      // Stop the timer if any
      stopActivityTimer();

      // You can now display this URL to the user or handle it as needed
    } catch (error) {
      console.error('Error ending conversation:', error);

      // Nothing was analysed, so a later attempt (header button, tab close) must be
      // allowed to try again rather than be dropped as a duplicate.
      releaseConversationEnd(claimedConversationId);

      if (isExpectedUnloadUploadError(error, viaUnload)) {
        // Expected: the tab closed before the upload finished. The conversation
        // is already in Firestore and emailSweep reconciles it — leave a
        // breadcrumb for traceability instead of an error report.
        Sentry.addBreadcrumb({
          category: 'endConversation',
          level: 'info',
          message: 'Upload aborted by page unload (recovered by emailSweep)',
          data: { conversationId: getFirestoreDocId(), errorCode: error.code }
        });
      } else {
        // Log genuine failures to Sentry
        Sentry.captureException(error, {
          tags: {
            function: 'endConversation',
            activityId: sharedState.selectedActivity,
            conversationId: getFirestoreDocId()
          },
          extra: {
            userId: getCurrentUserId(),
            elevenConversationId: elevenLabsConversationId,
            chatHistoryLength: conversation?.length || sharedState.chatHistory?.length || 0,
            statusCode: error.response?.status,
            errorCode: error.code
          }
        });
      }

      trackConversationEnded(null, sharedState.selectedActivity, feedbackData);
    } finally {
      // Close the toast when done (success or error)
      // react-simple-toasts v5+ returns an object with .close() method
      if (closeToast?.close) closeToast.close();
      // Always reset the upload flag when done (success or error)
      uploadInProgressRef.current = false;
    }

    // Return response so callers (e.g. ActivityApp) can access analysis results
    if (!resetState) {
      return response;
    }

    resetConversationState();
    // }
  }, [sharedState, setSharedState, uploadConversation, stopActivityTimer, updateLevelFromPlacementTest, resetConversationState]);

  return { endConversation };
};

export { useEndConversation, isExpectedUnloadUploadError };
